import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  loginUser,
  loginUserWithGoogle,
  sendLoginOtp,
  verifyLoginOtp,
} from "../apis/auth";
import { clearStoredAuth, getStoredToken, isTokenExpired } from "../utils/auth";
import { toast } from "react-toastify";
import {
  LogIn,
  AlertCircle,
  Eye,
  EyeOff,
  Mail,
  Lock,
  KeyRound,
} from "lucide-react";
import { GoogleOAuthProvider, GoogleLogin } from "@react-oauth/google";
import OtpInput from "react-otp-input";
import AuthBrand from "../components/AuthBrand";
import { useBranding } from "../contexts/BrandingContext";

const persistLoginSession = (responseData) => {
  // Fresh login: allow the once-per-session subscribe redirect to fire again
  sessionStorage.removeItem("subscribe_prompted");
  localStorage.setItem("token", responseData.token);
  localStorage.setItem("userAccessToken", responseData.token);
  if (responseData.user) {
    localStorage.setItem("user", JSON.stringify(responseData.user));
  }
  if (responseData.user?.role === "agent") {
    localStorage.setItem("userRole", "agent");
  } else {
    localStorage.removeItem("userRole");
  }
  localStorage.setItem("sections", JSON.stringify(responseData.sections || []));
  if (Array.isArray(responseData.permissions)) {
    localStorage.setItem("agentPermissions", JSON.stringify(responseData.permissions));
  } else {
    localStorage.removeItem("agentPermissions");
  }
};

const Login = () => {
  const [loginMethod, setLoginMethod] = useState("password");
  const [form, setForm] = useState({ email: "", password: "" });
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const navigate = useNavigate();
  const brand = useBranding();

  useEffect(() => {
    if (!brand.isBrandingReady) return;
    if (!brand.name) return;

    document.title = `Sign in — ${brand.name}`;
    const description =
      brand.tagline ||
      `Sign in to ${brand.name} to run WhatsApp broadcasts, automate chat, and track performance.`;
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "description";
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", description);

    const token = getStoredToken();
    if (token && isTokenExpired(token)) {
      clearStoredAuth();
    }
  }, [brand.isBrandingReady, brand.name, brand.tagline]);

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const resetOtpFlow = () => {
    setOtpSent(false);
    setOtp("");
    setError("");
  };

  const switchLoginMethod = (method) => {
    if (method === loginMethod) return;
    setLoginMethod(method);
    resetOtpFlow();
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    try {
      const response = await loginUser(form);
      if (response.data.success === false) {
        throw new Error(response.data.error || "Login failed.");
      }

      persistLoginSession(response.data);
      toast.success(response.data.message || "Login successful!");
      setTimeout(() => navigate("/dashboard"), 1000);
    } catch (err) {
      const message =
        err.response?.data?.error || err.message || "Login failed.";
      setError(message);
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!form.email) {
      toast.error("Please enter your email");
      return;
    }

    setIsLoading(true);
    setError("");
    try {
      const response = await sendLoginOtp({ email: form.email });
      if (response.data.success === false) {
        throw new Error(response.data.error || "Failed to send OTP");
      }

      toast.success(response.data.message || "OTP sent to your email");
      setOtpSent(true);
      setOtp("");
    } catch (err) {
      const message =
        err.response?.data?.error || err.message || "Failed to send OTP";
      setError(message);
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) {
      toast.error("Please enter the 6-digit OTP");
      return;
    }

    setIsLoading(true);
    setError("");
    try {
      const response = await verifyLoginOtp({ email: form.email, otp });
      if (response.data.success === false) {
        throw new Error(response.data.error || "Invalid OTP");
      }

      persistLoginSession(response.data);
      toast.success(response.data.message || "Login successful!");
      setTimeout(() => navigate("/dashboard"), 1000);
    } catch (err) {
      const message =
        err.response?.data?.error || err.message || "Invalid OTP";
      setError(message);
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setIsLoading(true);
    setError("");
    try {
      const response = await sendLoginOtp({ email: form.email });
      if (response.data.success) {
        toast.success("OTP resent to your email");
        setOtp("");
      } else {
        throw new Error(response.data.error || "Failed to resend OTP");
      }
    } catch (err) {
      const message = err.response?.data?.error || "Failed to resend OTP";
      setError(message);
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async (credentialResponse) => {
    setIsGoogleLoading(true);
    try {
      const data = { token: credentialResponse.credential };
      const response = await loginUserWithGoogle(data);
      if (response.data.success) {
        persistLoginSession(response.data);
        toast.success(response.data.message || "Google sign-in successful!");
        setTimeout(() => navigate("/dashboard"), 1000);
      } else {
        toast.error(response.data.error || "Google Login failed.");
      }
    } catch (err) {
      toast.error("Google Login failed. Try again.");
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const googleClientId = process.env.REACT_APP_CLIENT_ID;
  const showOtpStep = loginMethod === "otp" && otpSent;

  return (
    <div className="auth-page">
      <div className="auth-card">
        <AuthBrand />

        <h1>{showOtpStep ? "Check your email" : "Sign in"}</h1>
        <p className="auth-sub">
          {showOtpStep
            ? "Enter the 6-digit code we sent you"
            : "Access your WhatsApp dashboard"}
        </p>

        {error && (
          <div className="ping-banner red">
            <AlertCircle size={16} />
            <span>{error}</span>
          </div>
        )}

        {!showOtpStep && (
          <div className="ping-pill-tabs">
            <button
              type="button"
              className={`ping-ptab ${loginMethod === "password" ? "on" : ""}`}
              onClick={() => switchLoginMethod("password")}
            >
              <Lock size={14} />
              Password
            </button>
            <button
              type="button"
              className={`ping-ptab ${loginMethod === "otp" ? "on" : ""}`}
              onClick={() => switchLoginMethod("otp")}
            >
              <KeyRound size={14} />
              Email OTP
            </button>
          </div>
        )}

        {loginMethod === "password" && !showOtpStep && (
          <form onSubmit={handlePasswordLogin} className="auth-form">
            <div className="auth-field">
              <label htmlFor="email">Email</label>
              <div className="auth-input-wrap">
                <Mail className="auth-ico" size={16} />
                <input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="you@company.com"
                  autoComplete="email"
                  value={form.email}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="auth-field">
              <label htmlFor="password">Password</label>
              <div className="auth-input-wrap">
                <Lock className="auth-ico" size={16} />
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  value={form.password}
                  onChange={handleChange}
                  required
                />
                <button
                  type="button"
                  className="auth-eye"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div className="auth-row" style={{ justifyContent: "flex-end" }}>
              <Link to="/forget-password" className="auth-link">
                Forgot password?
              </Link>
            </div>

            <button type="submit" className="ping-btn ping-btn-green" disabled={isLoading}>
              {isLoading ? <span className="auth-spin" /> : <LogIn size={16} />}
              {isLoading ? "Signing in…" : "Sign in"}
            </button>
          </form>
        )}

        {loginMethod === "otp" && !showOtpStep && (
          <form onSubmit={handleSendOtp} className="auth-form">
            <div className="auth-field">
              <label htmlFor="otp-email">Email</label>
              <div className="auth-input-wrap">
                <Mail className="auth-ico" size={16} />
                <input
                  id="otp-email"
                  name="email"
                  type="email"
                  placeholder="you@company.com"
                  autoComplete="email"
                  value={form.email}
                  onChange={handleChange}
                  required
                />
              </div>
              <p className="auth-hint">We’ll send a 6-digit code to this inbox.</p>
            </div>

            <button type="submit" className="ping-btn ping-btn-green" disabled={isLoading}>
              {isLoading ? <span className="auth-spin" /> : <Mail size={16} />}
              {isLoading ? "Sending…" : "Send OTP"}
            </button>
          </form>
        )}

        {showOtpStep && (
          <form onSubmit={handleVerifyOtp} className="auth-form">
            <div className="auth-otp-mail">
              Code sent to
              <b>{form.email}</b>
            </div>

            <div className="auth-otp-boxes">
              <OtpInput
                value={otp}
                onChange={setOtp}
                numInputs={6}
                containerStyle={{
                  display: "flex",
                  justifyContent: "center",
                  gap: "8px",
                  width: "100%",
                }}
                renderInput={(props) => <input {...props} />}
                inputType="tel"
                shouldAutoFocus
              />
            </div>
            <p className="auth-hint" style={{ textAlign: "center" }}>
              Expires in 5 minutes.
            </p>

            <button
              type="submit"
              className="ping-btn ping-btn-green"
              disabled={isLoading || otp.length !== 6}
            >
              {isLoading ? <span className="auth-spin" /> : <LogIn size={16} />}
              {isLoading ? "Verifying…" : "Verify & sign in"}
            </button>

            <div className="auth-row">
              <button type="button" className="auth-link" onClick={resetOtpFlow} disabled={isLoading}>
                Change email
              </button>
              <button type="button" className="auth-link" onClick={handleResendOtp} disabled={isLoading}>
                Resend OTP
              </button>
            </div>
          </form>
        )}

        {!showOtpStep && (
          <>
            <div className="auth-or">OR</div>
            <div className="auth-google">
              <GoogleOAuthProvider clientId={googleClientId}>
                <GoogleLogin
                  onSuccess={handleGoogleLogin}
                  onError={() => toast.error("Google Login Failed")}
                  theme="outline"
                  text="continue_with"
                  width="352"
                  shape="rectangular"
                  size="large"
                  logo_alignment="left"
                  disabled={isGoogleLoading}
                />
              </GoogleOAuthProvider>
            </div>
          </>
        )}

        <div className="auth-foot">
          Don’t have an account?{" "}
          <Link to="/signup" className="auth-link">
            Sign up
          </Link>
          <div className="auth-help">
            Need help?{" "}
            {brand.supportEmail ? (
              <a href={`mailto:${brand.supportEmail}`} className="auth-link">
                Contact support
              </a>
            ) : (
              <span className="auth-link">Contact support</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
